import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import { streamProject } from '@/lib/realtime/stream'

/**
 * Keep a project's screens current while someone else works on it.
 *
 * Everything is invalidated rather than patched: the server told us something
 * changed, and the queries know how to fetch themselves. Patching a cache from
 * an event means teaching the client the server's business rules a second time,
 * which is how two sources of truth start disagreeing.
 */
export function useLiveProject(projectKey: string | null | undefined): void {
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!projectKey) return

    const handle = streamProject(projectKey, () => {
      void queryClient.invalidateQueries({ queryKey: ['board', projectKey] })
      void queryClient.invalidateQueries({ queryKey: ['timeline', projectKey] })
      void queryClient.invalidateQueries({ queryKey: ['activities'] })
      void queryClient.invalidateQueries({ queryKey: ['work-items'] })
    })

    return () => handle.close()
  }, [projectKey, queryClient])
}
